/** Property key constants for MGBW product properties (product.properties["mgbw_*"]). */

export const MGBW_IS_CONFIGURABLE = "mgbw_isConfigurable";
export const MGBW_PRODUCT_OPTIONS = "mgbw_productOptions";
export const MGBW_DEFAULT_SELECTIONS = "mgbw_defaultSelections";
export const MGBW_CONFIGURATOR_PRICE = "mgbw_configuratorPrice";

export const MGBW_CYLINDO_PRODUCT_ID = "mgbw_cylindoProductId";
export const MGBW_CYLINDO_ACCOUNT_ID = "mgbw_cylindoAccountId";
export const MGBW_CYLINDO_BACKGROUND_COLOR = "mgbw_cylindoBackgroundColor";
export const MGBW_CYLINDO_FEATURES = "mgbw_cylindoFeatures";

/**
 * Cylindo feature codes dropped during validation against the Cylindo Content API, sent to the
 * backend so it resolves the selection image instead of a Cylindo render. Transient input.
 */
export const MGBW_CYLINDO_UNSUPPORTED_INPUT = "mgbw_cylindoUnsupportedInput";

/** Persisted form of the unsupported Cylindo feature codes on cart / wish list / order lines. */
export const MGBW_CYLINDO_UNSUPPORTED = "mgbw_cylindoUnsupported";
export const MGBW_IMAGE_URL_MEDIUM = "mgbw_imageUrlMedium";
export const MGBW_IMAGE_URL_SMALL = "mgbw_imageUrlSmall";
export const MGBW_IMAGE_URL_LARGE = "mgbw_imageUrlLarge";
export const MGBW_CYLINDO_OG_IMAGE_URL = "mgbw_cylindoOgImageUrl";

export const MGBW_CROSS_SELL_TEXT = "mgbw_crossSellText";
export const MGBW_RELATED_PRODUCTS_TEXT = "mgbw_relatedProductsText";
export const MGBW_CUSTOMIZATION_BADGING_TEXT = "mgbw_customizationBadgingText";

/**
 * Configurator selections received from the backend as part of cart line properties in form of MgbwSelectionEntry objects.
 */
export const MGBW_SELECTIONS = "mgbw_selections";

/**
 * Configurator selections input passed to the backend in form of Record<string, string>.
 */
export const MGBW_SELECTIONS_INPUT = "mgbw_selectionsInput";

export const MGBW_CONFIGURATION_ITEM_NUMBER = "mgbw_configurationItemNumber";
export const MGBW_VALIDATION_STATUS = "mgbw_validationStatus";
export const MGBW_VALIDATION_MESSAGES = "mgbw_validationMessages";

/**
 * Product-level made-to-order shipping availability message (product.properties).
 * Used as the fallback when no selected option value carries its own message.
 */
export const MGBW_MTO_SHIPPING_AVAILABILITY = "mgbw_mtoShippingAvailability";

/**
 * Resolved shipping availability message stored on cart/wish list line properties.
 * Calculated by the backend on add/update from the selected option values, with
 * fallback to the product-level made-to-order message.
 */
export const MGBW_SHIPPING_AVAILABILITY = "mgbw_shippingAvailability";

/**
 * Canonical configurator validation status values. Must stay in sync with the
 * backend `ConfiguratorValidationStatus` enum wire values.
 */
export const MgbwValidationStatus = {
    valid: "Valid",
    stale: "Stale",
    unconfigured: "Unconfigured",
} as const;

export type MgbwValidationStatusValue = (typeof MgbwValidationStatus)[keyof typeof MgbwValidationStatus];

export const MGBW_IS_UNCONFIGURED = "mgbw_isUnconfigured";

export const MGBW_PRE_CONFIGURED_STYLES = "mgbw_preConfiguredStyles";

/** Customer's Own Leather (COL) approximate requirements text on the product. */
export const MGBW_COL = "mgbw_col";

/** Customer's Own Material (COM) approximate requirements text on the product. */
export const MGBW_COM = "mgbw_com";

/**
 * Query parameter used to pass the cart line ID to the PDP when reconfiguring an
 * existing cart line. The PDP reads this param to switch the Add to Cart button
 * into "Update Cart" mode. Survives the oid- URL sync because it lacks the "oid-" prefix.
 */
export const MGBW_UPDATE_CART_LINE_ID = "updateCartLineId";

/**
 * Query parameter used to pass the wish list line ID to the PDP when reconfiguring an
 * existing wish list line. The PDP reads this param (together with MGBW_UPDATE_WISHLIST_ID)
 * to switch the Add to Cart button into "Update List" mode. Survives the oid- URL sync
 * because it lacks the "oid-" prefix.
 */
export const MGBW_UPDATE_WISHLIST_LINE_ID = "updateWishListLineId";

/**
 * Query parameter used to pass the owning wish list ID to the PDP when reconfiguring an
 * existing wish list line. Paired with MGBW_UPDATE_WISHLIST_LINE_ID.
 */
export const MGBW_UPDATE_WISHLIST_ID = "updateWishListId";

/**
 * Query parameter used to pass the quantity from a cart or project line to the PDP
 * when reconfiguring an existing line. The PDP reads this param to pre-select the
 * quantity in the quantity selector. Survives the oid- URL sync because it lacks the
 * "oid-" prefix.
 */
export const MGBW_UPDATE_QTY = "qty";
